/**
 * Syncs filter state to URL search params.
 * Filters persist across page refreshes and can be shared via URL.
 *
 * Usage:
 *   const [filters, setFilter] = useUrlFilters({ period: '7', view: 'overview' })
 *   setFilter('period', '30')  // updates URL to ?period=30&view=overview
 */

import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

export function useUrlFilters<T extends Record<string, string>>(
  defaults: T,
): [T, (key: keyof T, value: string) => void, (updates: Partial<T>) => void] {
  const [searchParams, setSearchParams] = useSearchParams()

  // Build current values: URL params override defaults
  const current = { ...defaults } as T
  for (const key of Object.keys(defaults)) {
    const urlVal = searchParams.get(key)
    if (urlVal !== null) {
      (current as any)[key] = urlVal
    }
  }

  const setFilter = useCallback((key: keyof T, value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value === defaults[key]) {
        next.delete(key as string) // Remove default values to keep URL clean
      } else {
        next.set(key as string, value)
      }
      return next
    }, { replace: true })
  }, [setSearchParams, defaults])

  const setFilters = useCallback((updates: Partial<T>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      for (const [key, value] of Object.entries(updates)) {
        if (value === defaults[key as keyof T]) {
          next.delete(key)
        } else {
          next.set(key, value as string)
        }
      }
      return next
    }, { replace: true })
  }, [setSearchParams, defaults])

  return [current, setFilter, setFilters]
}
